import { useCustomizeHeaderContext } from 'contexts/CustomizeHeaderContext';
import React, {
  CSSProperties,
  PropsWithChildren,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useCustomHeaderTitle } from './useCustomHeaderTitle';
import * as useAPI from 'hooks/useAPI';
import * as api from 'config/api';
import { isAccountAggregated as isAccountAggregated_ } from 'components/features/home/Home';
import { useAppUIContext } from 'contexts/AppUIContext';
import Loading from 'components/commons/loading/Loading';
import { useRouting } from './useRouting';

const NotAggregatedPage = React.memo(({ title }: { title: string }) => {
  useCustomHeaderTitle(title);
  const { setUIClassName } = useAppUIContext();
  const {
    setWithBackIconWord,
    setWithBell,
    setWithNavigation,
  } = useCustomizeHeaderContext();
  useEffect(() => {
    const cleanUpBackIconWord = setWithBackIconWord();
    const cleanUpBell = setWithBell();
    const cleanUpNavigation = setWithNavigation();
    const { cleanup: cleanupUIClassName } = setUIClassName('ui-app-background');
    return () => {
      cleanUpBackIconWord();
      cleanUpBell();
      cleanUpNavigation();
      cleanupUIClassName();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const pushRouting = (({ pushRouting }) => (path: string) => () => {
    pushRouting({ path }, true);
  })(useRouting());
  return (
    <>
      <div className="ui-gradient-mt">
        <div className="max-w-md rounded-lg flex flex-col bg-white p-4 mt-8 mx-4">
          <div className="flex-grow items-center justify-center font-bold text-lg text-center mb-4">
            資産情報を登録してください
          </div>
          <div className="flex-grow text-sm">
            口座を連携して自分の資産を見える化しましょう。
          </div>
          <img
            width={200}
            height={150}
            src="/static/images/renkei.png"
            alt="連携画像"
            className="my-4 mx-auto"
          />
          <button
            onClick={pushRouting('/securityaccountslink')}
            className={`w-full flex items-center justify-center flex-1 bg-tradewell rounded-lg py-2 px-4 text-white font-bold cursor-pointer`}>
            口座・資産情報を登録する
          </button>
        </div>
      </div>
    </>
  );
});

const useBrokers = () => {
  const [state, setState] = useState<
    useAPI.State<'state', api.GetUserBrokerResultItem[]>
  >(useAPI.loading);

  const getBrokers = async () => {
    const res = await api.getUserBroker();
    setState(useAPI.loaded({ state: res.Items }));
  };

  return { brokers: state, getBrokers };
};

type AccountStatus = 'aggregated' | 'not_aggregated';

const isAccountAggregated = (
  bs: Parameters<typeof isAccountAggregated_>[0]
): AccountStatus => {
  return isAccountAggregated_(bs) ? 'aggregated' : 'not_aggregated';
};

export const useNotAggregatedPage = ({
  title,
  alreadyHitBrokers,
}: {
  title: string;
  alreadyHitBrokers:
    | { state: api.GetUserBrokerResultItem[] | 'loading' }
    | 'notHit';
}) => {
  const { brokers, getBrokers } = useBrokers();
  const brokersToAccountStatus = (
    bs: ReturnType<typeof useBrokers>['brokers']
  ): AccountStatus | 'loading' => {
    if (useAPI.isLoading(bs)) return 'loading';
    return isAccountAggregated(bs.state);
  };
  const accountStatus: AccountStatus | 'loading' = (() => {
    if (alreadyHitBrokers === 'notHit') {
      return brokersToAccountStatus(brokers);
    } else if (alreadyHitBrokers.state === 'loading') {
      return 'loading';
    } else {
      return isAccountAggregated(alreadyHitBrokers.state);
    }
  })();
  useEffect(() => {
    if (alreadyHitBrokers === 'notHit') {
      getBrokers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alreadyHitBrokers]);

  const NotAggregatedPageSwitcher = useMemo(
    () => ({
      children,
      loadingStyle,
    }: PropsWithChildren<{
      loadingStyle?: CSSProperties;
    }>): React.ReactElement => {
      switch (accountStatus) {
        case 'aggregated':
          return <>{children}</>;
        case 'not_aggregated':
          return <NotAggregatedPage {...{ title }} />;
        case 'loading':
          return <Loading loading style={loadingStyle} />;
      }
    },
    [accountStatus, title]
  );
  return { NotAggregatedPageSwitcher };
};
